import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import type { ReactNode, CSSProperties } from 'react';
import { FacebookSelector, FacebookCounter } from '../Reactions/Reactions';
import ClickOutSide from '@components/Globals/ClickOutSide';
import CorgeeModalStats from '@components/Globals/CorgeeModalStats';
import ConversationApi from './api/ConversationApi';
import './ConversationCardGrayBar.css';
import $ from 'jquery';
import { getSideMenuLocal } from '../utils/CorgeeHelper';
import { authStorage } from '@/lib';
import { buildDashboardUrl } from '@lib/conversationUrl';

// Fallback when the side menu has no Users item
const DEFAULT_USER_ICON = 'icons-menu-person';

// Nearest scrollable ancestor, or null when the page itself scrolls
const getScrollParent = (el: HTMLElement): HTMLElement | null =>
{
  for (let node = el.parentElement; node; node = node.parentElement)
  {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight
    )
    {
      return node;
    }
  }
  return null;
};

// Type definitions
interface ConversationReaction {
  conversationid?: number;
  ConversationID?: number;
  personid?: number;
  PersonID?: number;
  by?: string;
  Description?: string;
  emoji?: string;
  Reaction?: string;
  ImageName?: string;
  reactions?: ConversationReaction[];
  SocketConnectionId?: string;
  CardOwnerId?: number;
}

interface Conversation {
  ConversationId: number;
  TotalReplies: number;
  TotalTasks: number;
  TotalFavorites: number;
  Replies: Reply[];
  Reactions: ConversationReaction[];
  CreatedByID: number;
  UpdateConversationReaction?: boolean;
  Icon: string;
}

interface Reply {
  ReplyId: number;
  OriginalConversationId?: number;
}

interface RealItemTypes {
  Tasks: {
    Name: string;
    Icon: string;
  };
  Users: {
    Name: string;
    Icon: string;
  };
}

interface Dashboard {
  DashboardId: number;
  ProjectId?: number;
  ConfigValueId: number;
  Description: string;
}

interface ParentTask {
  Id: number;
  Title: string;
}

interface WarningUser {
  PersonName: string;
  ImageName: string;
  WarningCount: number;
}

interface AssignedUser {
  Title: string;
  ImageName: string;
}

interface ModalState {
  open: boolean;
  title: string;
  template: ReactNode[] | null;
}

interface ComponentState {
  me: number;
  name: string;
  imageProfile: string;
  realIconsResume: Record<string, unknown>;
  seeMoreReplies: boolean;
  seeMoreText?: string;
  classArrow?: string;
  modal: ModalState;
  conversation: Conversation;
  showSelector: boolean;
  showReaction: boolean;
  selectedReplyTab: 'all' | 'comments' | 'worklog' | 'activity' | 'metrics';
  isBeta: boolean;
  userIcon: string;
  taskIcon: string;
  tabsShowLeft: boolean;
  tabsShowRight: boolean;
  selectorPos: { right: number; bottom: number };
}

interface ConversationCardGrayBarProps {
  conversation: Conversation;
  api: unknown;
  totalFavorites?: number;
  totalReplies?: number;
  seeMoreRepliesGrayBar?: boolean;
  // Ground truth from the replies window (null = unknown, fall back to counts)
  repliesFullyShown?: boolean | null;
  realItemTypes: RealItemTypes;
  SocketConnectionId?: string;
  corgeeFunctions?: {
    onClickParentTasks?: (
      e: React.MouseEvent,
      conversationId: number,
      onCloseModal: () => void
    ) => void;
  };
  onUpdateConversation?: (
    conversationId: number,
    updates: Partial<Conversation>,
    skipUpdate?: boolean,
    source?: string
  ) => void;
  onSeeMoreReplies?: (flag: boolean) => void;
  updateMessageMoreReplies?: boolean;
  onHandleReplyTabSelected?: (tabName: 'all' | 'comments' | 'worklog' | 'activity' | 'metrics') => void;
  isLoadingMoreReplies?: boolean;
  config: {
    weburl: string;
    /** Set by host apps that are not the web SPA (Chat) */
    isChatHost?: boolean;
  };
  userIcon: string;
}

class ConversationCardGrayBar extends Component<ConversationCardGrayBarProps, ComponentState>
{
  private tabsRef = React.createRef<HTMLDivElement>();
  private tabsResizeObserver: ResizeObserver | null = null;
  private tabsArrowTimer: ReturnType<typeof setTimeout> | null = null;
  private reactionTriggerRef = createRef<HTMLSpanElement>();
  private replyBarRef = createRef<HTMLDivElement>();
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private repliesEverLoaded = false;
  private _isExpanding = false;
  // Reply bar offset captured on toggle: browser scroll anchoring shifts the
  // view when the list grows, which on a short page lands at its bottom
  private _pendingAnchor: { top: number; count: number } | null = null;

  constructor(props: ConversationCardGrayBarProps)
  {
    super(props);

    this.state = {
      me: 0,
      name: '',
      imageProfile: '',
      realIconsResume: {},
      userIcon: DEFAULT_USER_ICON,
      taskIcon: '',
      seeMoreReplies: props.seeMoreRepliesGrayBar || false,
      modal: {
        open: false,
        title: '',
        template: null,
      },
      conversation: props.conversation,
      showSelector: false,
      showReaction: true,
      selectedReplyTab: 'all',
      isBeta: localStorage.getItem('C2_isBeta') === 'true',
      tabsShowLeft: false,
      tabsShowRight: false,
      selectorPos: { right: 0, bottom: 0 },
    };

    this.onSeeMoreReplies = this.onSeeMoreReplies.bind(this);
    this.onGetTaskDashboards = this.onGetTaskDashboards.bind(this);
    this.onGetTaskParent = this.onGetTaskParent.bind(this);
    this.onGetTaskUsers = this.onGetTaskUsers.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
    this.onGetTaskWarnings = this.onGetTaskWarnings.bind(this);
    this.onClickContainingDashboards = this.onClickContainingDashboards.bind(this);
    this.onClickParentTasks = this.onClickParentTasks.bind(this);
    this.onHoverReactions = this.onHoverReactions.bind(this);
    this.closeHoverReactions = this.closeHoverReactions.bind(this);
    this.startCloseTimer = this.startCloseTimer.bind(this);
    this.onSelectReactions = this.onSelectReactions.bind(this);
    this.onUpdateMessageMoreReplies = this.onUpdateMessageMoreReplies.bind(this);
    this.countReplies = this.countReplies.bind(this);
    this.countAllReplies = this.countAllReplies.bind(this);
    this.onGetPhaseSelected = this.onGetPhaseSelected.bind(this);
    this.onHandleReplyTabSelected = this.onHandleReplyTabSelected.bind(this);
    this.updateTabArrows = this.updateTabArrows.bind(this);
    this.scrollTabs = this.scrollTabs.bind(this);
  }

  componentDidMount(): void
  {
    this.setState({
      me: parseInt(authStorage.getPersonId() || '0', 10),
      name: sessionStorage.getItem('C2_personName') || authStorage.getGivenName() || '',
      imageProfile: sessionStorage.getItem('C2_profileImage') || 'Images/user.png',
    });

    const realIconsResume = localStorage.getItem('C2_realIconsResume');
    if (realIconsResume)
    {
      this.setState({ realIconsResume: JSON.parse(realIconsResume) });
    }

    const userIcon = getSideMenuLocal()?.find(x => x.ConfigValueID === 15)?.Icon;
    this.setState({ userIcon: userIcon || DEFAULT_USER_ICON });

    const taskIcon = getSideMenuLocal()?.find(x => x.ConfigValueID === 5)?.Icon;
    this.setState({ taskIcon: taskIcon || '' });

    const el = this.tabsRef.current;
    if (el)
    {
      el.addEventListener('scroll', this.updateTabArrows, { passive: true });
      this.tabsResizeObserver = new ResizeObserver(this.updateTabArrows);
      this.tabsResizeObserver.observe(el);
      this.updateTabArrows();
    }
  }

  componentWillUnmount(): void
  {
    const el = this.tabsRef.current;
    if (el) el.removeEventListener('scroll', this.updateTabArrows);
    this.tabsResizeObserver?.disconnect();
    if (this.tabsArrowTimer) clearTimeout(this.tabsArrowTimer);
  }

  updateTabArrows(): void
  {
    if (this.tabsArrowTimer) clearTimeout(this.tabsArrowTimer);
    this.tabsArrowTimer = setTimeout(() =>
    {
      const el = this.tabsRef.current;
      if (!el) return;
      this.setState({
        tabsShowLeft: el.scrollLeft > 0,
        tabsShowRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      });
    }, 60);
  }

  scrollTabs(direction: 'left' | 'right'): void
  {
    const el = this.tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -120 : 120, behavior: 'smooth' });
  }

  componentDidUpdate(prevProps: ConversationCardGrayBarProps): void
  {
    if (this.props.conversation)
    {
      // Check if UpdateConversationReaction just became true
      if (
        this.props.conversation.UpdateConversationReaction === true &&
        prevProps.conversation?.UpdateConversationReaction !== true
      )
      {
        const copyConversation = { ...this.props.conversation };
        copyConversation.UpdateConversationReaction = false;
        this.setState({ showReaction: false, conversation: copyConversation });

        setTimeout(() =>
        {
          this.setState({ showReaction: true });
        });
      }

      // Check if updateMessageMoreReplies just became true
      if (this.props.updateMessageMoreReplies && !prevProps.updateMessageMoreReplies)
      {
        this.onUpdateMessageMoreReplies();
      }
    }

    this.restoreAnchorAfterToggle(prevProps);
  }

  restoreAnchorAfterToggle(prevProps: ConversationCardGrayBarProps): void
  {
    const anchor = this._pendingAnchor;
    if (!anchor) return;

    if (this.countReplies() === anchor.count)
    {
      // List unchanged: drop the anchor only once the fetch is done
      if (prevProps.isLoadingMoreReplies && !this.props.isLoadingMoreReplies)
      {
        this._pendingAnchor = null;
      }
      return;
    }

    this._pendingAnchor = null;

    const bar = this.replyBarRef.current;
    if (!bar) return;

    const delta = bar.getBoundingClientRect().top - anchor.top;
    if (Math.abs(delta) <= 1) return;

    const scroller = getScrollParent(bar);
    if (scroller)
    {
      scroller.scrollTop += delta;
    }
    else
    {
      window.scrollBy(0, delta);
    }
  }

  onGetPhaseSelected(): number
  {
    const { conversation } = this.props;
    try
    {
      const infoConv = $(`[data-react-conv='${conversation.ConversationId}']`);
      let convPhaseId = 0;

      if (infoConv && infoConv.length)
      {
        const phaseSelected = infoConv.find('.react-tabs__tab--selected');
        if (phaseSelected && phaseSelected.length > 0)
        {
          convPhaseId = parseInt($(phaseSelected).attr('data-conversationphaseid') || '0');
        }
      }
      return convPhaseId;
    }
    catch (error)
    {
      console.warn('Error getting phase selected:', error);
      return 0;
    }
  }

  onClickContainingDashboards = (
    e: React.MouseEvent,
    dashboardId: number,
    projectId?: number,
    configValueId?: number,
  ): void =>
  {
    e.preventDefault();
    const { config } = this.props;

    // Only a non-web host (Chat) may point these links to another origin
    const cfg = config as { weburl?: string; isChatHost?: boolean } | undefined;
    const weburl = cfg?.isChatHost ? (cfg.weburl || '').trim().replace(/\/chat\/?$/i, '/') : '';
    const urlResult = buildDashboardUrl(dashboardId, projectId, configValueId, weburl);

    window.open(urlResult, '_blank');
  };

  onClickParentTasks = (e: React.MouseEvent, conversationId: number): void =>
  {
    e.preventDefault();

    if (this.props.corgeeFunctions?.onClickParentTasks)
    {
      this.props.corgeeFunctions.onClickParentTasks(e, conversationId, this.onCloseModal);
    }
  };

  onGetTaskDashboards = (e: React.MouseEvent): void =>
  {
    e.preventDefault();

    ConversationApi.getContainingDashboards(this.props.api as any, {
      conversationId: this.props.conversation.ConversationId,
    }).then((response: Dashboard[]) =>
    {
      if (response)
      {
        const _templeteTemp: ReactNode[] = [];

        for (let index = 0; index < response.length; index++)
        {
          _templeteTemp.push(
            <div
              key={`item-${index}`}
              onClick={e =>
                this.onClickContainingDashboards(
                  e,
                  response[index]!.DashboardId,
                  response[index]!.ProjectId,
                  response[index]!.ConfigValueId,
                )
              }
            >
              <div className="conv-list-item">
                <a className="metadata-link card-metadata-item-link card-link card-stats">
                  <div className="card-metadata-info-layout-row card-stats">
                    <span className="card-metadata-info-layout-cell card-metadata-item-dashboard-type-icon card-stats">
                      <span className="icons-corgee-dashboard"></span>
                    </span>
                    <span className="card-metadata-info-layout-cell card-metadata-item-dashboard-type-text card-stats">
                      <span>{response[index]!.Description}</span>
                    </span>
                  </div>
                </a>
              </div>
            </div>,
          );
        }

        const _template: ReactNode[] = [];
        if (_templeteTemp.length > 0)
        {
          _template.push(
            <div className="conversation-metadata-list">
              <div className="card-metadata-window-items-list">{_templeteTemp}</div>
            </div>,
          );
        }

        if (_template.length === 0)
        {
          _template.push(
            <div className="card-metadata-window-items-list">
              <span className="no-results">Item is not available.</span>
            </div>,
          );
        }

        this.setState({
          modal: { open: true, title: 'Containing dashboards', template: _template },
        });
      }
    });
  };

  onGetTaskParent = (e: React.MouseEvent, name: string): void =>
  {
    e.preventDefault();

    ConversationApi.getParentTasks(this.props.api as any, {
      conversationId: this.props.conversation.ConversationId,
      type: 1,
    }).then((response: ParentTask[]) =>
    {
      if (response)
      {
        const _templateTemp: ReactNode[] = [];

        for (let index = 0; index < response.length; index++)
        {
          _templateTemp.push(
            <div
              key={`item-${index}`}
              onClick={e => this.onClickParentTasks(e, response[index]!.Id)}
            >
              <div className="conv-list-item">
                <a className="metadata-link card-metadata-item-link card-link card-stats">
                  <div className="card-metadata-info-layout-row card-stats">
                    <span className="card-metadata-info-layout-cell card-metadata-item-dashboard-type-icon card-stats">
                      <span className="icons-corgee-hierarchy"></span>
                    </span>
                    <span className="card-metadata-info-layout-cell card-metadata-item-dashboard-type-text card-stats">
                      <span>{response[index]!.Title}</span>
                    </span>
                  </div>
                </a>
              </div>
              <span className="table-items-metadata"></span>
            </div>,
          );
        }

        const _template: ReactNode[] = [];
        if (_templateTemp.length > 0)
        {
          _template.push(
            <div className="conversation-metadata-list">
              <div className="card-metadata-window-items-list">{_templateTemp}</div>
            </div>,
          );
        }

        if (_template.length === 0)
        {
          _template.push(
            <div className="card-metadata-window-items-list">
              <span className="no-results">There is no parent task.</span>
            </div>,
          );
        }

        this.setState({ modal: { open: true, title: `Parent ${name}`, template: _template } });
      }
    });
  };

  onGetTaskUsers = (e: React.MouseEvent, name: string): void =>
  {
    e.preventDefault();

    ConversationApi.getParentTasks(this.props.api as any, {
      conversationId: this.props.conversation.ConversationId,
      type: 3,
    }).then((response: AssignedUser[]) =>
    {
      if (response)
      {
        const _templateTemp: ReactNode[] = [];

        for (let index = 0; index < response.length; index++)
        {
          const style: CSSProperties = {
            backgroundImage: `url(${response[index]!.ImageName})`,
            borderRadius: '50% 50%',
            backgroundSize: '100%',
            backgroundPosition: 'center',
            height: '25px',
            width: '25px',
            display: 'inline-block',
          };

          _templateTemp.push(
            <div key={`item-${index}`}>
              <div className="user-details">
                <div className="user-credential">
                  <div className="user-credential-col img-col">
                    <div className="user-avatar imageSize">
                      <div style={style}></div>
                    </div>
                  </div>
                  <div className="user-credential-col">
                    <div className="user-name">
                      <span>{response[index]!.Title}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
          );
        }

        const _template: ReactNode[] = [];
        if (_templateTemp.length > 0)
        {
          _template.push(
            <div className="conversation-metadata-list">
              <div className="format-metadata-table">{_templateTemp}</div>
            </div>,
          );
        }

        if (_template.length === 0)
        {
          _template.push(
            <div className="card-metadata-window-items-list">
              <span className="no-results">There are no assigned users.</span>
            </div>,
          );
        }

        this.setState({ modal: { open: true, title: `Assigned ${name}`, template: _template } });
      }
    });
  };

  onGetTaskWarnings = (e: React.MouseEvent): void =>
  {
    e.preventDefault();

    ConversationApi.getConversationWarnings(this.props.api as any, {
      conversationId: this.props.conversation.ConversationId,
    }).then((response: WarningUser[]) =>
    {
      if (response)
      {
        const _templateTemp: ReactNode[] = [];

        for (let index = 0; index < response.length; index++)
        {
          const warnedUser = response[index]!;

          const profilePictureStyle: CSSProperties = {
            backgroundImage: `url(${warnedUser.ImageName})`,
            borderRadius: '50% 50%',
            backgroundSize: '100%',
            backgroundPosition: 'center',
            height: '25px',
            width: '25px',
            display: 'inline-block',
          };

          const warningCountStyle: CSSProperties = {
            ...profilePictureStyle,
            backgroundImage: '',
            display: 'grid',
            alignContent: 'center',
            justifyContent: 'center',
            color: '#fff',
            backgroundColor: '#62aacb',
            marginRight: '6px',
            fontSize: '14px',
          };

          const warningNameStyle: CSSProperties = {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '206px',
            display: 'block',
          };

          _templateTemp.push(
            <div key={`item-${index}`}>
              <div className="user-details">
                <div className="user-credential">
                  <div className="user-credential-col img-col">
                    <div className="user-avatar imageSize">
                      <div style={warningCountStyle}>{warnedUser.WarningCount}</div>
                    </div>
                  </div>
                  <div className="user-credential-col img-col">
                    <div className="user-avatar imageSize">
                      <div style={profilePictureStyle}></div>
                    </div>
                  </div>
                  <div className="user-credential-col">
                    <div className="user-name">
                      <span className="user-warning-name-infowindow" style={warningNameStyle}>
                        {warnedUser.PersonName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
          );
        }

        const _template: ReactNode[] = [];
        if (_templateTemp.length > 0)
        {
          _template.push(
            <div className="conversation-metadata-list">
              <div className="format-metadata-table-warnings">{_templateTemp}</div>
            </div>,
          );
        }

        if (_template.length === 0)
        {
          _template.push(
            <div className="card-metadata-window-items-list">
              <span className="no-results">There are no users with warnings.</span>
            </div>,
          );
        }

        this.setState({ modal: { open: true, title: 'Users with warnings', template: _template } });
      }
    });
  };

  onSeeMoreReplies = (e?: React.MouseEvent): void =>
  {
    if (this.props.isLoadingMoreReplies) return;

    const { conversation, totalReplies } = this.props;

    if (e)
    {
      try
      {
        $(e.target).blur();
      }
      catch (error)
      {
        console.warn('Error blurring element:', error);
      }
      e.preventDefault();
    }

    const convPhaseId = this.onGetPhaseSelected();
    let newTotalReplies = conversation.TotalReplies;

    if (totalReplies !== undefined && totalReplies !== null)
    {
      newTotalReplies = totalReplies;
    }

    const allTotalReplies = newTotalReplies + (convPhaseId === 0 ? conversation.TotalTasks : 0);
    const countReplies = this.countReplies();
    const flag = this.props.repliesFullyShown ?? (countReplies === allTotalReplies);

    this._isExpanding = !flag;

    const bar = this.replyBarRef.current;
    this._pendingAnchor = bar
      ? { top: bar.getBoundingClientRect().top, count: countReplies }
      : null;

    this.setState({}, () =>
    {
      if (this.props.onSeeMoreReplies)
      {
        this.props.onSeeMoreReplies(!flag);
      }
    });
  };

  onUpdateMessageMoreReplies = (): void =>
  {
    const convPhaseId = this.onGetPhaseSelected();
    const filteredReplies =
      convPhaseId === 0
        ? this.props.conversation.Replies
        : this.props.conversation.Replies.filter(x => (x.OriginalConversationId || 0) === 0);

    const newSeeMoreReplies =
      this.props.conversation.TotalReplies +
        (convPhaseId === 0 ? this.props.conversation.TotalTasks : 0) <=
      filteredReplies.length
        ? false
        : true;

    const seeMoreText = newSeeMoreReplies ? 'See more replies' : 'See less replies';
    const classArrow = newSeeMoreReplies ? 'arrow_drop_down' : 'arrow_drop_up';

    this.setState({ seeMoreText, classArrow });
  };

  onCloseModal = (): void =>
  {
    this.setState({ modal: { open: false, title: '', template: null } });
  };

  onHoverReactions = (): void =>
  {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.reactionTriggerRef.current)
    {
      const rect = this.reactionTriggerRef.current.getBoundingClientRect();
      this.setState({
        selectorPos: {
          right: window.innerWidth - rect.right - 4,
          bottom: window.innerHeight - rect.top + 4,
        },
      });
    }
    this.setState({ showSelector: true });
  };

  closeHoverReactions = (): void =>
  {
    this.setState({ showSelector: false });
  };

  startCloseTimer = (): void =>
  {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(this.closeHoverReactions, 150);
  };

  onSelectReactions = (emoji: string): void =>
  {
    const conversation = { ...this.state.conversation };
    const index = conversation.Reactions.findIndex(rp => rp.personid === this.state.me);
    const reactions = conversation.Reactions;

    const reaction: ConversationReaction = {
      ConversationID: conversation.ConversationId,
      PersonID: this.state.me,
      Description: this.state.name,
      Reaction: emoji,
      reactions: [],
      SocketConnectionId: this.props.SocketConnectionId,
      CardOwnerId: conversation.CreatedByID,
    };

    if (index > -1)
    {
      const existingEmoji =
        conversation.Reactions[index]!.emoji || conversation.Reactions[index]!.Reaction;
      if (existingEmoji === emoji)
      {
        conversation.Reactions = reactions.filter(rp => rp.personid !== this.state.me);
        reaction.reactions = conversation.Reactions;

        this.setState({ showSelector: false, conversation });
        ConversationApi.deleteConversationReaction(this.props.api as any, reaction);
        this.props.onUpdateConversation?.(
          conversation.ConversationId,
          { Reactions: conversation.Reactions } as any,
          true,
        );
      }
      else
      {
        conversation.Reactions = reactions.filter(rp => rp.personid !== this.state.me);
        conversation.Reactions.push({
          conversationid: conversation.ConversationId,
          personid: this.state.me,
          by: this.state.name,
          emoji,
          ImageName: this.state.imageProfile,
        });

        reaction.reactions = conversation.Reactions;

        this.setState({ showSelector: false, conversation });
        ConversationApi.updateConversationReaction(this.props.api as any, reaction);
        this.props.onUpdateConversation?.(
          conversation.ConversationId,
          { Reactions: conversation.Reactions } as any,
          true,
        );
      }
    }
    else
    {
      conversation.Reactions.push({
        conversationid: conversation.ConversationId,
        personid: this.state.me,
        by: this.state.name,
        emoji,
        ImageName: this.state.imageProfile,
      });

      reaction.reactions = conversation.Reactions;

      this.setState({ showSelector: false, conversation });
      ConversationApi.insertConversationReaction(this.props.api as any, reaction);
      this.props.onUpdateConversation?.(
        conversation.ConversationId,
        { Reactions: conversation.Reactions } as any,
        true,
      );
    }
  };

  countReplies = (): number =>
  {
    const { conversation } = this.props;
    const convPhaseId = this.onGetPhaseSelected();
    const replies =
      convPhaseId === 0
        ? (conversation.Replies || [])
        : (conversation.Replies || []).filter(
          x => (x.OriginalConversationId || 0) === 0 || x.OriginalConversationId === undefined,
        );

    const uniqueIds: number[] = [];

    for (let index = 0; index < replies.length; index++)
    {
      if (uniqueIds.indexOf(replies[index]!.ReplyId) < 0)
      {
        uniqueIds.push(replies[index]!.ReplyId);
      }
    }

    return uniqueIds.length;
  };

  countAllReplies = (newTotalReplies: number): number =>
  {
    const { conversation } = this.props;

    try
    {
      const selectedPhaseId = $(`[data-react-conv='${conversation.ConversationId}']`)
        .find('.react-tabs__tab--selected')
        .attr('data-conversationphaseid');

      if (parseInt(selectedPhaseId || '0') > 0)
      {
        return newTotalReplies;
      }
      else
      {
        return conversation.TotalTasks + newTotalReplies;
      }
    }
    catch (error)
    {
      console.warn('Error counting all replies:', error);
      return newTotalReplies;
    }
  };

  onHandleReplyTabSelected = (
    e: React.MouseEvent,
    tabName: 'all' | 'comments' | 'worklog' | 'activity' | 'metrics',
  ): void =>
  {
    e.preventDefault();
    this.setState({ selectedReplyTab: tabName }, () =>
    {
      if (this.props.onHandleReplyTabSelected)
      {
        this.props.onHandleReplyTabSelected(tabName);
      }
    });
  };

  render(): ReactNode
  {
    const {
      totalFavorites,
      totalReplies,
      realItemTypes,
      conversation,
      seeMoreRepliesGrayBar,
      repliesFullyShown,
      isLoadingMoreReplies,
    } = this.props;
    const { showSelector, name, userIcon, taskIcon, isBeta } = this.state;
    const numReplies = 3;

    if (conversation.Replies?.length > 0)
    {
      this.repliesEverLoaded = true;
    }

    const clickOutSideWhiteList = [
      { type: 'class', value: 'selectorFacebook' },
      { type: 'class', value: 'icon-reaction-chat' },
      { type: 'class', value: 'icon-reactions-default' },
    ];

    let isDarkMode = false;
    if (localStorage.getItem('C2_enableDarkMode'))
    {
      isDarkMode = localStorage.getItem('C2_enableDarkMode') === 'true';
    }

    if (
      localStorage.getItem('C2_darkModeChat') &&
      (window.location.href.indexOf('/Chat/') > 0 ||
        window.location.href.indexOf('chat') > 0 ||
        window.location.href.indexOf('Chat') > 0)
    )
    {
      isDarkMode = localStorage.getItem('C2_darkModeChat') === 'true';
    }

    if (!conversation.Reactions)
    {
      conversation.Reactions = [];
    }

    let reactedByMe = false;
    if (conversation.Reactions && conversation.Reactions.length > 0)
    {
      const names = conversation.Reactions.map(r => r.by);
      if (names.includes(name))
      {
        reactedByMe = true;
      }
    }

    const reactedByMeColor = isDarkMode
      ? reactedByMe
        ? '#ddd'
        : '#999999'
      : reactedByMe
        ? '#999999'
        : '#ddd';

    const _newTotalFavorites =
      totalFavorites !== undefined && totalFavorites !== null
        ? totalFavorites
        : conversation.TotalFavorites;
    void _newTotalFavorites;

    let newTotalReplies = conversation.TotalReplies;
    if (totalReplies !== undefined && totalReplies !== null)
    {
      newTotalReplies = totalReplies;
    }

    const allTotalReplies = this.countAllReplies(newTotalReplies);
    const conversationReplies = this.countReplies();

    if (this._isExpanding && allTotalReplies === conversationReplies)
    {
      this._isExpanding = false;
    }

    // "See less replies" only once the whole thread is visible; while replies
    // remain hidden the link keeps offering "See more replies" (each click
    // reveals 3 more). Comparing shown vs total is the single source of truth —
    // the old _isExpanding flag flipped the label on the FIRST click, before
    // everything was shown.
    // Ground truth (window covers the fetched thread) wins; the count compare
    // is only a fallback — TotalReplies can overcount what the fetch returns
    // (layer-hidden or disabled replies, per-phase lists vs global counters).
    const isExpanded =
      repliesFullyShown ?? (allTotalReplies === conversationReplies && allTotalReplies > numReplies);
    // Link visible while there is something to expand, or something to collapse;
    // an empty (or fully visible short) phase shows no toggle at all.
    const showRepliesToggle =
      repliesFullyShown != null
        ? !repliesFullyShown || conversationReplies > numReplies
        : allTotalReplies > numReplies && this.repliesEverLoaded;
    void seeMoreRepliesGrayBar;
    const seeMoreTextDynamic = isExpanded ? 'See less replies' : 'See more replies';
    const classArrowDynamic = isExpanded ? 'arrow_drop_up' : 'arrow_drop_down';

    return (
      <div onMouseLeave={this.closeHoverReactions}>
        <div className="corgee_com_card-stats">
          <div className="corgee_com_card-stats-list">
            <span
              role="button"
              tabIndex={0}
              className="comments active anchore_unbinded enable-help-tooltip"
              data-title={`${newTotalReplies} comments in conversation`}
            >
              <span className="icon-comment-icon"></span>
              <span className="replies-total-replies">{newTotalReplies}</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              className="approved tasks-embedded active anchore_unbinded enable-help-tooltip"
              data-title={`${conversation.TotalTasks} embedded ${realItemTypes.Tasks.Name} in conversation`}
            >
              <span className={`${taskIcon || realItemTypes.Tasks.Icon} tasks-embedded-icon`}></span>
              <span className="replies-total-tasks">{conversation.TotalTasks}</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={this.onGetTaskDashboards}
              className="enable-help-tooltip"
              data-title="Containing dashboards"
            >
              <span className="icons-corgee-dashboard"></span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={e => this.onGetTaskParent(e, realItemTypes.Tasks.Name)}
              className="real-tasks-name-item enable-help-tooltip"
              data-title={`Parent ${realItemTypes.Tasks.Name}`}
            >
              <span className="icons-corgee-hierarchy"></span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={e => this.onGetTaskUsers(e, realItemTypes.Users.Name)}
              className="real-users-name-item enable-help-tooltip"
              data-title={`Assigned ${realItemTypes.Users.Name}`}
            >
              <span className={`${userIcon} tasks-embedded-icon`} id="ccgb-taskusers-span"></span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={e => this.onGetTaskWarnings(e)}
              className="real-users-name-item enable-help-tooltip"
              data-title="Warnings"
            >
              <span className="icons-menu" id="ccgb-warnedusers-span">
                <i className="material-icons" style={{ fontSize: '12px' }}>
                  warning
                </i>
              </span>
            </span>
          </div>

          {conversation.Reactions && (
            <div className="corgee_com_card-stats-reactions">
              {conversation.Reactions.length > 0 && (
                <div className="selectorFacebook">
                  {React.createElement(FacebookCounter as any, {
                    includeLabel: true,
                    user: name,
                    id: String(conversation.ConversationId),
                    counters: conversation.Reactions,
                    bg: isDarkMode ? '#2d2f2f' : '#fff',
                    onclick: this.onHoverReactions,
                    marginTopNeeded: false,
                  })}
                </div>
              )}
              <ClickOutSide
                onClickOutSide={this.closeHoverReactions}
                whiteList={clickOutSideWhiteList as any}
                className="  flex align-middle justify-center"
              >
                <span
                  ref={this.reactionTriggerRef}
                  onMouseEnter={this.onHoverReactions}
                  onMouseLeave={this.startCloseTimer}
                  className="single-reply icon-reactions-default"
                  style={{ color: reactedByMeColor }}
                >
                  thumb_up_off
                </span>
              </ClickOutSide>
            </div>
          )}

          {showSelector &&
            ReactDOM.createPortal(
              <div
                className="selectorFacebook"
                onMouseEnter={() => { if (this.closeTimer) clearTimeout(this.closeTimer); }}
                onMouseLeave={this.startCloseTimer}
                style={{
                  position: 'fixed',
                  right: this.state.selectorPos.right,
                  bottom: this.state.selectorPos.bottom,
                  zIndex: 9999,
                }}
              >
                <FacebookSelector
                  reactions={['like', 'love', 'haha', 'wow', 'sad', 'angry', 'eyes']}
                  bg={isDarkMode ? '#2d2f2f' : '#fff'}
                  iconsize={38}
                  onselect={this.onSelectReactions}
                />
              </div>,
              document.body,
            )}

          <CorgeeModalStats
            className=""
            openModal={this.state.modal.open}
            title={this.state.modal.title}
            onClose={this.onCloseModal}
            isDraggable={true}
            closeOnEscape={true}
            hasMaxHeight={true}
            width={350}
            height={120}
          >
            <div>
              {this.state.modal.template &&
                this.state.modal.template.map((element, idx) => (
                  <React.Fragment key={idx}>{element}</React.Fragment>
                ))}
            </div>
          </CorgeeModalStats>
        </div>

        <div className="corgee_com_card-reply-bar-container" ref={this.replyBarRef}>
          <div className="corgee_com_card-reply-tabs-wrapper">
            <button
              className={`reply-tabs-arrow reply-tabs-arrow--left${this.state.tabsShowLeft ? '' : ' reply-tabs-arrow--hidden'}`}
              onClick={() => this.scrollTabs('left')}
              aria-label="Scroll tabs left"
            >
              <i className="material-icons">chevron_left</i>
            </button>
            <div className="corgee_com_card-reply-tabs" ref={this.tabsRef}>
              <div
                onClick={e => this.onHandleReplyTabSelected(e, 'all')}
                className={`reply-tab ${this.state.selectedReplyTab === 'all' ? 'selected' : ''}`}
                data-tab="all"
              >
                All
              </div>
              <div
                onClick={e => this.onHandleReplyTabSelected(e, 'comments')}
                className={`reply-tab ${this.state.selectedReplyTab === 'comments' ? 'selected' : ''}`}
                data-tab="comments"
              >
                Comments
              </div>
              <div
                onClick={e => this.onHandleReplyTabSelected(e, 'worklog')}
                className={`reply-tab ${this.state.selectedReplyTab === 'worklog' ? 'selected' : ''}`}
                data-tab="worklog"
              >
                Work Log
              </div>
              <div
                onClick={e => this.onHandleReplyTabSelected(e, 'activity')}
                className={`reply-tab ${this.state.selectedReplyTab === 'activity' ? 'selected' : ''}`}
                data-tab="activity"
              >
                Activity
              </div>
              {isBeta && (
                <div
                  onClick={e => this.onHandleReplyTabSelected(e, 'metrics')}
                  className={`reply-tab ${this.state.selectedReplyTab === 'metrics' ? 'selected' : ''}`}
                  data-tab="metrics"
                >
                  Metrics
                </div>
              )}
            </div>
            <button
              className={`reply-tabs-arrow reply-tabs-arrow--right${this.state.tabsShowRight ? '' : ' reply-tabs-arrow--hidden'}`}
              onClick={() => this.scrollTabs('right')}
              aria-label="Scroll tabs right"
            >
              <i className="material-icons">chevron_right</i>
            </button>
          </div>

          {showRepliesToggle && (
            <div className="corgee_com_card-see-reply-left">
              <div className="corgee_com_card-stats-see-replies">
                <span
                  role="button"
                  tabIndex={0}
                  aria-disabled={isLoadingMoreReplies || undefined}
                  aria-live="polite"
                  onClick={this.onSeeMoreReplies}
                  className={`anchore_unbinded see-all-replies corgee_com_card-see-more-btn${isLoadingMoreReplies ? ' corgee_com_card-see-more-btn--loading' : ''}`}
                >
                  {isLoadingMoreReplies ? (
                    <>
                      <span className="corgee_com_card-see-more-spinner" aria-hidden="true"></span>
                      Loading replies...
                    </>
                  ) : (
                    <>
                      {seeMoreTextDynamic}
                      <span className="md-font material-icons">{classArrowDynamic}</span>
                    </>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ConversationCardGrayBar;
